import { h, Component, ComponentChild } from 'preact'
import Table from '../table/Table'
import ButtonControl from '../button-control'
import axios from 'axios'
import AlertComponent from '../ui-components/AlertComponent'
import Loader from '../ui-components/Loader'
import { EditModal } from './EditModal'
import { DeleteModal } from './DeleteModal'

interface Type {
  id: number,
  name: string,
  eventsCount: number
}

interface State {
  types: Array<Type>,
  error?: string,
  typeToDelete?: Type,
  typeToEdit?: Type,
  showNewTypeModal: boolean,
  isLoading: boolean,
  messageType?: string,
  message?: string,
  modalErrorMessage?: string,
}

interface Props {}

interface Row {
  header?: string,
  accessor: string,
  cell: string|ComponentChild,
  colspan?: number
}
const DEFAULT_ERROR_MESSAGE = 'Something went wrong, please try again.'

class EventTypeOverview extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      types: [],
      isLoading: true,
      showNewTypeModal: false
    }
    this.onClickEditModalInput = this.onClickEditModalInput.bind(this)
  }

  async componentDidMount () {
    this.setState({ messageType: undefined })
    try {
      const res = await axios.get('/settings/eventTypes', {
        headers: {
          'Accept': 'application/json'
        }
      })
      this.setState({ types: res.data.types, isLoading: false })
    } catch (err) {
      this.setState({ message: 'Failed to fetch event types.', messageType: 'error', isLoading: false })
      throw err
    }
  }

  async deleteType (typeToDelete: Type) {
    this.setState({ messageType: undefined })
    const successMessage = `Event type '${typeToDelete.name}' has been deleted!`
    try {
      await axios.delete(`/settings/eventType/${typeToDelete.id}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      this.setState({ types: this.state.types.filter((type: Type) => typeToDelete !== type), message: successMessage, messageType: 'success' })
    } catch (err) {
      this.setState({ message: DEFAULT_ERROR_MESSAGE, messageType: 'error', isLoading: false })
    }
  }

  async editType (typeToEdit: Type, newName: string) {
    this.setState({ messageType: undefined, modalErrorMessage: undefined })
    const successMessage = `Event type '${typeToEdit.name}' has been renamed to '${newName}'!`
    if (!newName) {
      this.setState({ modalErrorMessage: 'Please enter event type', isLoading: false })
      return
    }
    try {
      const res = await axios.post(
        `/settings/eventType/${typeToEdit.id}`,
        { oldName: typeToEdit.name, newName: newName }
      )
      const types = res.data?.types || this.state.types
      this.setState({ types: types, message: successMessage, messageType: 'success', isLoading: false, typeToEdit: undefined })
    } catch (err) {
      let errorMessage = err.response?.data?.errors ? err.response.data.errors : DEFAULT_ERROR_MESSAGE
      this.setState({ modalErrorMessage: errorMessage, isLoading: false })
    }
  }

  async createType (newType: string) {
    this.setState({ messageType: undefined, modalErrorMessage: undefined })
    const successMessage = `Event type '${newType}' has been added!`
    if (!newType) {
      this.setState({ modalErrorMessage: 'Please enter event type', isLoading: false })
      return
    }
    try {
      const res = await axios.put(`/settings/eventType`, { typeName: newType })
      this.setState({ types: res.data.types, message: successMessage, messageType: 'success', isLoading: false, showNewTypeModal: false })
    } catch (err) {
      this.setState({ message: DEFAULT_ERROR_MESSAGE, messageType: 'error', isLoading: false, showNewTypeModal: false })
    }
  }

  onClickEditModalInput () {
    if (this.state.modalErrorMessage) {
      this.setState({ modalErrorMessage: undefined })
    }
  }

  render (props: Props, { types, messageType, message, isLoading, typeToDelete, typeToEdit, showNewTypeModal, modalErrorMessage }: State) {
    const isVisibleFor = messageType === 'success' ? 5000 : false

    const rows = types.map((type: Type): Array<Row> => [{
      header: 'Event types',
      accessor: 'type-name',
      cell: <span class='pill pill--uppercase qa-type-pill'>{type.name}</span>
    }, {
      header: 'Used by',
      accessor: 'used-by',
      cell: <span>{type.eventsCount.toString()} {type.eventsCount === 1 ? 'Event' : 'Events'}</span>
    }, {
      header: 'Actions',
      accessor: 'actions',
      cell: <span>
        <ButtonControl newStyle title='Edit' onClick={() => this.setState({ typeToEdit: type, messageType: undefined })} />
        {/*
          commented out till delete will be implemented in community
         <ButtonControl title={'Delete'} variant={'warning'} onClick={() => this.setState({ typeToDelete: type })} />
        */}
      </span>
    }])
    rows.push([{
      accessor: 'new-type',
      cell: <ButtonControl newStyle title='Add new event type' variant='brand' onClick={() => this.setState({ showNewTypeModal: true })} />,
      colspan: 3
    }])

    return <div class='event-types-overview qa-event-types-overview'>
      <div class='control-header'>
        <h2>Event Types</h2>
      </div>
      {isLoading && <Loader />}
      <div class='control-body'>
        {
          messageType
            ? <AlertComponent messageType={messageType} message={message} sticky renderHTML isVisibleFor={isVisibleFor} />
            : null
        }
        {typeToDelete ? <DeleteModal type={typeToDelete.name}
          onClose={() => this.setState({ typeToDelete: undefined })}
          onDelete={() => this.setState({ typeToDelete: undefined, isLoading: true }, () => this.deleteType(typeToDelete))}
        /> : null}
        {typeToEdit ? <EditModal type={typeToEdit.name}
          onClose={() => this.setState({ typeToEdit: undefined, modalErrorMessage: undefined })}
          onSave={(newName: string) => this.setState({ isLoading: true }, () => this.editType(typeToEdit, newName))}
          onClick={this.onClickEditModalInput}
          error={modalErrorMessage}
        /> : null}
        {showNewTypeModal ? <EditModal onClose={() => this.setState({ showNewTypeModal: false, modalErrorMessage: undefined })}
          onSave={(newName: string) => this.setState({ isLoading: true }, () => this.createType(newName))}
          onClick={this.onClickEditModalInput}
          error={modalErrorMessage} /> : null}
        <Table rows={rows} classes={['event-types__table']} />
      </div>
    </div>
  }
}

export default EventTypeOverview
